/**
 * "Will he last?" — the availability model (§7.6, FR-29 – FR-31).
 *
 * This is the term that produces BeerSheets-like "don't reach, he'll be back to
 * you" behaviour. Given an ADP mean and standard deviation, it estimates the
 * probability a player is still on the board at the user's next pick, and turns
 * that into the only version of the number anyone actually acts on: take him
 * now, or wait.
 *
 * Pure module — no I/O.
 */

import type { AdpData, AdpRow, DraftState, PlayerId, Position } from './types.ts';

/**
 * Standard normal CDF, Abramowitz & Stegun 26.2.17. Accurate to ~7.5e-8, which
 * is several orders of magnitude better than the ADP data feeding it.
 */
export function normalCdf(x: number): number {
  const sign = x < 0 ? -1 : 1;
  const z = Math.abs(x) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * z);
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t +
      0.254829592) *
      t *
      Math.exp(-z * z);
  return 0.5 * (1 + sign * y);
}

export interface AvailabilityInput {
  adp: number;
  stdev: number;
  /** Number of drafts the ADP was sampled from — drives the confidence label. */
  times_drafted: number;
  /** The pick number we are asking about. */
  target_pick: number;
  /** The pick number the draft is currently at. */
  current_pick: number;
}

export type AdpConfidence = 'high' | 'medium' | 'low' | 'none';

export interface AvailabilityResult {
  /** P(player is still available at `target_pick`). */
  probability: number;
  confidence: AdpConfidence;
  /** Human-readable basis, e.g. "ADP 24.3 ± 6.1 over 1,127 drafts". */
  basis: string;
}

/**
 * P(available at pick N) = P(this player's draft position > N).
 *
 * Modelling draft position as normal around ADP is a simplification, but a
 * defensible one: ADP is itself an average over many drafts, and the spread
 * that FFC reports is exactly the dispersion we need. The important corrections
 * are the two conditioning steps below.
 */
export function availabilityAt(input: AvailabilityInput): AvailabilityResult {
  const { adp, times_drafted, target_pick, current_pick } = input;

  // FFC reports stdev 0 for players drafted very few times. A zero-variance
  // model claims certainty it has not earned, so floor the spread relative to
  // how deep the player goes — later picks are genuinely more variable.
  const stdev = Math.max(input.stdev, 1.5 + adp * 0.06);

  const confidence = adpConfidence(times_drafted, adp);

  // Condition on what the draft has already shown us. A player whose ADP is 20
  // and who is still there at pick 40 is not a 20-ADP player any more, and
  // pretending otherwise is how a tool tells you to wait on someone who is
  // about to be taken. Renormalising by P(survived to now) fixes it.
  const pSurvivedToNow = 1 - normalCdf((current_pick - adp) / stdev);
  const pSurvivedToTarget = 1 - normalCdf((target_pick - adp) / stdev);

  let probability =
    pSurvivedToNow <= 1e-6
      ? // Already well past his ADP and still here: the model has nothing left
        // to say, so fall back to a flat pessimistic prior driven by distance.
        Math.max(0, 1 - (target_pick - current_pick) / 24)
      : Math.min(1, pSurvivedToTarget / pSurvivedToNow);

  if (!Number.isFinite(probability)) probability = 0;

  return {
    probability: clamp01(probability),
    confidence,
    basis: `ADP ${adp.toFixed(1)} ± ${stdev.toFixed(1)} over ${times_drafted.toLocaleString()} drafts`,
  };
}

/**
 * FR-31: ADP is a poor fit late in drafts and in unusual formats, so the model
 * must say how much it should be trusted. Both axes matter — a player sampled
 * from 8 drafts is noise, and *any* player past roughly pick 150 is noise
 * because late-round ADP is dominated by autodraft and abandoned rooms.
 */
export function adpConfidence(timesDrafted: number, adp: number): AdpConfidence {
  if (timesDrafted <= 0) return 'none';
  if (adp > 160) return 'low';
  if (timesDrafted >= 50 && adp <= 120) return 'high';
  if (timesDrafted >= 20) return 'medium';
  return 'low';
}

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

// ---------------------------------------------------------------------------
// Snake draft pick arithmetic
// ---------------------------------------------------------------------------

/**
 * All pick numbers belonging to a draft slot, in order.
 *
 * Snake and linear are both handled because Sleeper reports the type and some
 * leagues genuinely use linear. Auction is out of scope in v1 (§12).
 */
export function picksForSlot(
  slot: number,
  teams: number,
  rounds: number,
  type: string,
): number[] {
  const out: number[] = [];
  for (let round = 1; round <= rounds; round++) {
    if (type === 'linear') {
      out.push((round - 1) * teams + slot);
    } else {
      const isReverse = round % 2 === 0;
      const positionInRound = isReverse ? teams - slot + 1 : slot;
      out.push((round - 1) * teams + positionInRound);
    }
  }
  return out;
}

/** The user's next two picks, given where the draft currently is. */
export function upcomingPicks(
  slot: number | null,
  teams: number,
  rounds: number,
  type: string,
  currentPickNo: number,
): { next: number | null; after: number | null; all: number[] } {
  if (slot === null) return { next: null, after: null, all: [] };
  const all = picksForSlot(slot, teams, rounds, type);
  const upcoming = all.filter((p) => p >= currentPickNo);
  return { next: upcoming[0] ?? null, after: upcoming[1] ?? null, all };
}

/**
 * The picks a player has to survive for a wait decision.
 *
 * `upcomingPicks` intentionally includes the current pick because the draft
 * header and schedule need to know when the user is on the clock. A wait
 * decision is different: if the user can pick now, "will he make it back?"
 * means the next pick after this one. When the user is not on the clock, the
 * first upcoming pick remains the correct target.
 */
export function waitingPicks(
  slot: number | null,
  teams: number,
  rounds: number,
  type: string,
  currentPickNo: number,
): { next: number | null; after: number | null; all: number[] } {
  const upcoming = upcomingPicks(slot, teams, rounds, type, currentPickNo);
  if (upcoming.next !== currentPickNo) return upcoming;

  const currentIndex = upcoming.all.indexOf(currentPickNo);
  return {
    next: currentIndex >= 0 ? upcoming.all[currentIndex + 1] ?? null : upcoming.after,
    after: currentIndex >= 0 ? upcoming.all[currentIndex + 2] ?? null : null,
    all: upcoming.all,
  };
}

// ---------------------------------------------------------------------------
// The actionable version (FR-30)
// ---------------------------------------------------------------------------

export type WaitVerdict = 'take-now' | 'likely-gone' | 'can-wait' | 'safe-to-wait' | 'unknown';

export interface WaitAdvice {
  verdict: WaitVerdict;
  probability_next: number;
  probability_after: number;
  confidence: AdpConfidence;
  /** One sentence the UI can render verbatim (§8 requires this be explainable). */
  sentence: string;
}

export function waitAdvice(
  playerName: string,
  adpRow: AdpRow | undefined,
  draft: DraftState,
  teams: number,
  rounds: number,
  type: string,
): WaitAdvice {
  const { next, after } = waitingPicks(
    draft.my_draft_slot,
    teams,
    rounds,
    type,
    draft.current_pick_no,
  );

  if (!adpRow || next === null) {
    return {
      verdict: 'unknown',
      probability_next: 0,
      probability_after: 0,
      confidence: 'none',
      sentence: `No ADP data for ${playerName} — can't estimate whether he lasts.`,
    };
  }

  const base = {
    adp: adpRow.adp,
    stdev: adpRow.stdev,
    times_drafted: adpRow.times_drafted,
    current_pick: draft.current_pick_no,
  };
  const atNext = availabilityAt({ ...base, target_pick: next });
  const atAfter =
    after === null
      ? { probability: 0, confidence: atNext.confidence, basis: atNext.basis }
      : availabilityAt({ ...base, target_pick: after });

  let verdict: WaitVerdict;
  let sentence: string;

  if (atNext.probability < 0.2) {
    verdict = 'take-now';
    sentence = `${playerName} is very unlikely to reach your next pick (${pct(atNext.probability)}) — take him now or lose him.`;
  } else if (atNext.probability < 0.5) {
    verdict = 'likely-gone';
    sentence = `${playerName} is more likely gone than not by pick ${next} (${pct(atNext.probability)} to survive).`;
  } else if (atAfter.probability >= 0.5) {
    verdict = 'safe-to-wait';
    sentence = `${playerName} should still be there two picks from now (${pct(atAfter.probability)}) — you can spend this pick elsewhere.`;
  } else {
    verdict = 'can-wait';
    sentence = `${playerName} will probably last to pick ${next} (${pct(atNext.probability)}) but not to ${after ?? '—'}.`;
  }

  if (atNext.confidence === 'low' || atNext.confidence === 'none') {
    sentence += ' (Thin ADP sample — treat as a hint, not a read.)';
  }

  return {
    verdict,
    probability_next: atNext.probability,
    probability_after: atAfter.probability,
    confidence: atNext.confidence,
    sentence,
  };
}

const pct = (p: number): string => `${Math.round(p * 100)}%`;

// ---------------------------------------------------------------------------
// Opportunity cost (FR-30)
// ---------------------------------------------------------------------------

export interface OpportunityCost {
  /** Expected best points at this position if we wait one turn. */
  expected_if_wait: number;
  /** Points of the candidate right now. */
  value_now: number;
  /** value_now − expected_if_wait. Positive means taking now gains you points. */
  delta: number;
  /** The players the model expects to still be there at the next turn. */
  fallbacks: { player_key: PlayerId; name: string; probability: number; points: number }[];
}

/**
 * The comparison that actually decides a pick: take player A now, or take the
 * best player at that position who survives to the next turn?
 *
 * Expected value of waiting is the probability-weighted best survivor, computed
 * greedily down the position list — the chance the *best* remaining option is
 * player i is the chance i survives times the chance everyone better did not.
 */
export function opportunityCost(
  candidate: { player_key: PlayerId; name: string; points: number },
  samePositionPool: { player_key: PlayerId; name: string; points: number; adp: AdpRow | undefined }[],
  draft: DraftState,
  teams: number,
  rounds: number,
  type: string,
): OpportunityCost {
  const { next } = waitingPicks(draft.my_draft_slot, teams, rounds, type, draft.current_pick_no);

  const fallbacks: OpportunityCost['fallbacks'] = [];
  let expected = 0;
  let noBetterSurvives = 1;

  if (next !== null) {
    const ordered = samePositionPool
      .filter((p) => p.player_key !== candidate.player_key)
      .sort((a, b) => b.points - a.points)
      .slice(0, 12);

    for (const p of ordered) {
      if (!p.adp) continue;
      const { probability } = availabilityAt({
        adp: p.adp.adp,
        stdev: p.adp.stdev,
        times_drafted: p.adp.times_drafted,
        target_pick: next,
        current_pick: draft.current_pick_no,
      });
      const isBest = noBetterSurvives * probability;
      expected += isBest * p.points;
      noBetterSurvives *= 1 - probability;
      if (probability > 0.05 && fallbacks.length < 3) {
        fallbacks.push({ player_key: p.player_key, name: p.name, probability, points: p.points });
      }
      if (noBetterSurvives < 0.01) break;
    }
  }

  return {
    expected_if_wait: Math.round(expected * 10) / 10,
    value_now: candidate.points,
    delta: Math.round((candidate.points - expected) * 10) / 10,
    fallbacks,
  };
}

/**
 * Expected best projected points still available at each position at the user's
 * next pick.
 *
 * This is the "what do I get if I wait?" number, and it is what turns the
 * timing term from a flat discount into a real opportunity cost. `opportunityCost`
 * above answers the same question for one candidate against one pool and is what
 * the player panel renders; this computes it once per position for the whole
 * board, which is what the engine needs and what keeps the pass O(n) rather than
 * O(n²).
 *
 * The candidate himself is deliberately *included* in his own position's
 * expectation. "If I wait, he might still be there" is a real outcome and
 * excluding it would overstate the cost of waiting on the best player at a
 * position — exactly the case where the answer matters most.
 *
 * The greedy walk is the same as `opportunityCost`: the chance player i is the
 * best one left is the chance he survives times the chance nobody better did.
 */
export interface SurvivorModel {
  /**
   * Expected points of the best player still available at `position` when the
   * user next picks.
   */
  expectedBest(position: Position): number | undefined;
  /** The pick this model is answering about, or null if it cannot be known. */
  targetPick: number | null;
}

/** Precomputed per position: survival odds paired with points, best first. */
interface PositionOdds {
  player_key: PlayerId;
  points: number;
  probability: number;
}

export function buildSurvivorModel(
  pool: { player_key: PlayerId; position: Position; points: number; rank?: number | null }[],
  adp: Map<PlayerId, AdpRow>,
  draft: DraftState,
  teams: number,
  rounds: number,
  type: string,
): SurvivorModel {
  const { next } = waitingPicks(draft.my_draft_slot, teams, rounds, type, draft.current_pick_no);
  const byPosition = new Map<Position, PositionOdds[]>();

  if (next !== null) {
    const grouped = new Map<Position, typeof pool>();
    for (const entry of pool) {
      const list = grouped.get(entry.position) ?? [];
      list.push(entry);
      grouped.set(entry.position, list);
    }

    for (const [position, entries] of grouped) {
      // Only the top of each position can plausibly be the best survivor, and
      // capping the walk is what keeps this affordable to run per candidate.
      const ordered = [...entries]
        .sort((a, b) => b.points - a.points)
        .slice(0, 24)
        .flatMap((entry) => {
          const row = adp.get(entry.player_key);

          // Without ADP, fall back to the board's own ordering: assume the room
          // drafts roughly in rank order. That is a much weaker claim than ADP
          // — it cannot know that this room reaches for quarterbacks — but it
          // preserves the one fact this model exists to supply, which is that
          // deep positions survive to your next pick and thin ones do not.
          //
          // FR-66 is why this matters: with both Workers disabled there is no
          // ADP at all, and a survivor model that goes silent would take the
          // lookahead with it. Callers that make a *claim about a player* from
          // this (the "he won't last" sentence) gate on real ADP instead; this
          // fallback only feeds structural comparisons between positions.
          const centre = row?.adp ?? entry.rank ?? null;
          if (centre === null) return [];
          const spread = row?.stdev ?? Math.max(6, (next - draft.current_pick_no) * 0.6);

          return [{
            player_key: entry.player_key,
            points: entry.points,
            probability: availabilityAt({
              adp: centre,
              stdev: spread,
              times_drafted: row?.times_drafted ?? 0,
              target_pick: next,
              current_pick: draft.current_pick_no,
            }).probability,
          }];
        });
      byPosition.set(position, ordered);
    }
  }

  const expectedBest = (position: Position): number | undefined => {
    const ordered = byPosition.get(position);
    if (!ordered || ordered.length === 0) return undefined;

    let expected = 0;
    let noBetterSurvives = 1;
    let weight = 0;

    for (const entry of ordered) {
      const isBest = noBetterSurvives * entry.probability;
      expected += isBest * entry.points;
      weight += isBest;
      noBetterSurvives *= 1 - entry.probability;
      if (noBetterSurvives < 0.01) break;
    }

    // Renormalise by the probability mass actually accumulated. Without this,
    // a position where everyone is likely to be gone reports a near-zero
    // expectation, which would price waiting as catastrophic at every position
    // late in a draft rather than at the ones that are genuinely thin.
    return weight > 0.05 ? expected / weight : undefined;
  };

  return { expectedBest, targetPick: next };
}

/**
 * The whole board's worth of "what do I get if I wait?", as a plain map.
 *
 * The candidate himself is deliberately *included* in his own position's
 * expectation. "If I wait, he might still be there" is a real outcome, and
 * excluding it would overstate the cost of waiting on the best player at a
 * position — exactly the case where the answer matters most. See
 * `lookahead.ts` for why the lookahead makes the same choice for a different
 * reason.
 */
export function expectedBestByPosition(
  pool: { player_key: PlayerId; position: Position; points: number }[],
  adp: Map<PlayerId, AdpRow>,
  draft: DraftState,
  teams: number,
  rounds: number,
  type: string,
): Map<Position, number> {
  const model = buildSurvivorModel(pool, adp, draft, teams, rounds, type);
  const out = new Map<Position, number>();
  for (const position of new Set(pool.map((p) => p.position))) {
    const value = model.expectedBest(position);
    if (value !== undefined) out.set(position, value);
  }
  return out;
}

/** Index ADP rows by resolved Sleeper id for O(1) lookup during scoring. */
export function indexAdp(adp: AdpData | null): Map<PlayerId, AdpRow> {
  const out = new Map<PlayerId, AdpRow>();
  if (!adp) return out;
  for (const row of adp.rows) {
    if (row.player_key) out.set(row.player_key, row);
  }
  return out;
}
